<template>
  <ion-page>
    <WhiteHeader
      :back-button="false"
      title="Mes missions"
      v-model:temporality="$data.temporality"
      :class="{ 'no-pointer-events': isLoading }"
    />

    <!-- Only display month selector when on past missions page -->
    <ion-item
      v-if="temporality === 'past'"
      class="month-selector"
      :class="{ 'no-pointer-events': isLoading }"
    >
      <ion-icon :icon="calendarNumberOutline"></ion-icon>
      <ion-select
        v-model="selectedDate"
        ok-text="Valider"
        cancel-text="Annuler"
        interface="action-sheet"
        @ionChange="doRefresh()"
      >
        <ion-select-option
          v-for="date in dates"
          :value="date[0]"
          :key="date[0]"
        >
          {{ date[1] }}
        </ion-select-option>
      </ion-select>
      <small style="opacity: 0.6">Changer...</small>
    </ion-item>

    <ion-content ref="content">
      <time-report-timer
        ref="timeReportTimer"
        v-if="displayTimeReportsTimerMode"
        v-show="temporality === 'present'"
      />

      <ion-refresher
        class="refresher refresher-planning"
        slot="fixed"
        @ionRefresh="doRefresh($event)"
        @click="doRefresh($event)"
        ><ion-refresher-content></ion-refresher-content
      ></ion-refresher>

      <div
        :class="{
          'list-card': true,
          'reversed': temporality === 'past',
        }"
      >
        <div
          v-for="mission in filteredPlanning"
          :key="(mission.kind || mission.type || '') + '-' + mission.id"
        >
          <ChooseGoodCard class="card-planning" :mission="mission" />
        </div>

        <!-- Completed missions accordion (only shown for present temporality) -->
        <ion-accordion-group
          v-if="completedMissions.length > 0"
          @ionChange="onAccordionToggle"
        >
          <ion-accordion class="completed-missions">
            <ion-item slot="header">
              <ion-icon
                slot="start"
                :icon="checkmarkDoneCircleOutline"
                color="success"
              ></ion-icon>

              <ion-label>Missions terminées</ion-label>

              <ion-badge slot="end" class="missions-badge">
                <strong>{{ completedMissionsLength }}</strong>
              </ion-badge>
            </ion-item>

            <ion-list slot="content" class="ion-no-padding">
              <div
                v-for="mission in completedMissions"
                :key="(mission.kind || mission.type || '') + '-' + mission.id"
              >
                <ChooseGoodCard class="card-planning" :mission="mission" />
              </div>
            </ion-list>
          </ion-accordion>
        </ion-accordion-group>
      </div>

      <ion-label
        v-if="!filteredPlanning.length && !completedMissions.length"
        class="align-text-center-screen"
      >
        <div>
          Aucune mission
          <span v-if="temporality === 'present'">prévue aujourd'hui.</span>
          <span v-else-if="temporality === 'past'">
            terminée à afficher sur la période.
          </span>
          <span v-else-if="temporality === 'future'">à venir.</span>
        </div>
      </ion-label>
    </ion-content>
  </ion-page>
</template>

<script>
import {
  IonLabel,
  IonAccordionGroup,
  IonAccordion,
  IonItem,
  IonList,
  IonBadge,
  IonIcon,
  IonSelect,
  IonSelectOption,
} from '@ionic/vue';
import {
  forceRefreshPlanning,
  getPlanning,
} from '@/services/driver/missionsOrders';
import WhiteHeader from '@/components/driver/header/WhiteHeader';
import ChooseGoodCard from '@/components/driver/planning/ChooseGoodCard';
import TimeReportTimer from '@/components/driver/planning/TimeReportTimer';
import {
  checkmarkDoneCircleOutline,
  calendarNumberOutline,
} from 'ionicons/icons';
import utils from '@/services/utils/Utils';
import dayjs from 'dayjs';
import * as Sentry from '@sentry/vue';

const PLANNING_REFRESH_RATE = 60 * 1000; // 1 minute
const DEFAULT_DATE = 'DEFAULT'; // Default = current and previous week

export default {
  name: 'PlanningChauffeur',

  components: {
    ChooseGoodCard,
    WhiteHeader,
    IonLabel,
    IonAccordionGroup,
    IonAccordion,
    IonItem,
    IonList,
    IonBadge,
    IonIcon,
    IonSelect,
    IonSelectOption,
    TimeReportTimer,
  },

  setup() {
    return {
      checkmarkDoneCircleOutline,
      calendarNumberOutline,
    };
  },

  data() {
    return {
      isLoading: false,
      planning: [],
      temporality: 'present',
      displayTimeReportsTimerMode: false,
      // Remember, `selectedDate` is only used when temporality === 'past'
      selectedDate: DEFAULT_DATE, // Default = current and previous week
    };
  },

  // Launch auto-refresh when page gets displayed
  ionViewDidEnter() {
    this.doRefresh();

    // auto refresh planning every minute
    this.refreshInterval = setInterval(() => {
      // Auto-refresh not wanted when past and selectedDate is not default
      if (this.temporality === 'past' && this.selectedDate !== DEFAULT_DATE) {
        return;
      }

      this.doRefresh();
    }, PLANNING_REFRESH_RATE);
  },

  // Stop auto-refresh when page quits
  ionViewDidLeave() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
    }
  },

  async mounted() {
    this.isLoading = true;
    try {
      this.planning = await getPlanning();
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      this.isLoading = false;
    }

    this.displayTimeReportsTimerMode =
      window.localStorage.getItem('timeReportsTimerMode') === 'true';
  },

  /**
   * Filter missions into ongoing and completed (or all for past/future)
   */
  computed: {
    filteredPlanning() {
      if (!this.planning || !this.planning.length) return [];

      if (this.temporality !== 'present') {
        return this.planning.filter(mission =>
          this.showMission(mission.start, mission.end),
        );
      }

      return this.filterMissions(false);
    },

    // completed missions have rental_coupons emited
    completedMissions() {
      // if not present, don't show any missions as completed
      if (this.temporality !== 'present') return [];

      return this.filterMissions(true);
    },

    /**
     * Can't juste use completedMissions.length since we have transport items
     */
    completedMissionsLength() {
      let length = 0;

      this.completedMissions.forEach(mission => {
        length += mission.transport_items
          ? Object.keys(mission.transport_items).length
          : 1;
      });

      return length;
    },

    dates() {
      return [
        [DEFAULT_DATE, 'Période par défaut'],
        ...utils.generateDateList(),
      ];
    },
  },

  methods: {
    /**
     * Filter missions for the present time period with transport items filtering
     */
    filterMissions(completed) {
      if (!this.planning || !this.planning.length) return [];

      return (
        [...this.planning]
          .filter(mission => this.showMission(mission.start, mission.end))
          .map(mission => {
            const transportItems = mission.transport_items;

            // simple case, its a reservation or absence (no transport items)
            if (!transportItems || !Object.keys(transportItems).length) {
              return mission;
            }

            const newTransportItems = {};
            const tiIdentifiers = Object.keys(transportItems);
            tiIdentifiers.forEach(key => {
              const ti = transportItems[key];

              // if transport is for multiple day, always add ti when ongoing mission
              // else, add ti if rental_coupons match wanted completed status
              if (
                (mission.start !== mission.end && completed === false) ||
                ti.rental_coupons === completed
              ) {
                newTransportItems[key] = ti;
              }
            });

            // return the transport, with the filtered list of transport items
            return {
              ...mission,
              // keep track of total ti, we need it for card display
              totalTransportItems: tiIdentifiers.length,
              // can have rental_coupons on transport directly, since we have filtered items
              rental_coupons: completed,
              transport_items: newTransportItems,
            };
          })
          // from mapped data, we can now filter missions based on rental_coupons
          .filter(mission => {
            // if type absence, display them always on ongoing missions
            if (mission.type === 'absence') {
              return completed === false;
            }

            // if transport don't have any items, don't display it
            if (
              'transport_items' in mission &&
              !Object.keys(mission.transport_items).length
            ) {
              return false;
            }

            // if start and end dates are different, always display in ongoing missions
            if (mission.start !== mission.end) {
              return completed === false;
            }

            return mission.rental_coupons === completed;
          })
      );
    },

    async doRefresh(event) {
      this.isLoading = true;

      try {
        let month = null;
        if (this.temporality === 'past' && this.selectedDate !== DEFAULT_DATE) {
          month = this.selectedDate;
        }

        this.planning = await forceRefreshPlanning(month);

        if (
          this.$refs.timeReportTimer &&
          'loadExistingTimers' in this.$refs.timeReportTimer
        ) {
          this.$refs.timeReportTimer.loadExistingTimers();
        }
      } catch (error) {
        Sentry.captureException(error);
        console.log('Unable to force refresh planning.');
      }

      this.isLoading = false;

      if (event && event.target && event.target.complete) {
        event.target.complete();
      }
    },

    /**
     * Return the temporality of a mission (past, present, future)
     */
    getTemporality(start, end) {
      const now = dayjs();

      // Check if mission is in the past
      if (dayjs(end).isBefore(now, 'day')) {
        return 'past';
      }

      return dayjs(start).isAfter(now, 'day') ? 'future' : 'present';
    },

    /**
     * Allows to know if a mission can be displayed according to the selected time frame, Also
     * updates the variable for whether a mission or missions are displayed
     */
    showMission(missionStart, missionEnd) {
      return this.getTemporality(missionStart, missionEnd) === this.temporality;
    },

    /**
     * After toggling on completed missions, scroll a bit down to show the content
     */
    onAccordionToggle(event) {
      if (!event || !event.detail || !event.detail.value) return;

      this.$nextTick(() => {
        setTimeout(() => {
          const accordionEl = event.target;
          if (!accordionEl || !accordionEl.scrollIntoView) return;
          accordionEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 200); // Real animation duration is 300ms, but looks better with 200ms
      });
    },
  },

  watch: {
    temporality(newVal, oldVal) {
      // if we only check between current and future missions we don't need to refresh list every time
      if (newVal !== 'past' && oldVal !== 'past') return;
      this.doRefresh();
    },
  },
};
</script>

<style lang="scss" scoped>
.month-selector {
  --border-style: none;

  ion-icon {
    margin-right: 8px;
  }
}

.list-card {
  display: flex;
  margin: 0;
  padding: 0;
  flex-direction: column;

  &.reversed {
    flex-direction: column-reverse;
  }
}

ion-accordion.completed-missions {
  &::part(header) {
    --ion-background-color: #f8f9fa;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    border-left: 4px solid var(--ion-color-success);
    border-radius: 8px;
    margin: 10px;
  }

  &.accordion-expanded::part(header) {
    margin-bottom: 0;
  }
}

.missions-badge {
  background-color: #dbeafe;
  color: #0369a1;
  padding: 4px 8px;
  border-radius: 50%;
}
</style>
